import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { Sidebar } from './Sidebar'
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator'

export function Settings() {
  const { user, userInfo } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  useEffect(() => {
    // PASSWORD_RECOVERY 이벤트 감지
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true)
      }
    })

    return () => {
      authListener.subscription.unsubscribe()
    }
  }, [])

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      // 비밀번호 리셋 모드가 아닐 때는 현재 비밀번호 확인
      if (!isPasswordRecovery) {
        if (!currentPassword || !newPassword || !confirmPassword) {
          throw new Error('모든 필드를 입력해주세요')
        }
      } else {
        if (!newPassword || !confirmPassword) {
          throw new Error('모든 필드를 입력해주세요')
        }
      }

      // 새 비밀번호가 일치하는지 확인
      if (newPassword !== confirmPassword) {
        throw new Error('새 비밀번호가 일치하지 않습니다')
      }

      // 비밀번호 최소 길이 확인
      if (newPassword.length < 6) {
        throw new Error('비밀번호는 최소 6자 이상이어야 합니다')
      }

      // 비밀번호 리셋 모드가 아닐 때만 현재 비밀번호와 비교
      if (!isPasswordRecovery) {
        // 새 비밀번호가 현재 비밀번호와 다른지 확인
        if (newPassword === currentPassword) {
          throw new Error('새 비밀번호는 현재 비밀번호와 달라야 합니다')
        }

        // 현재 비밀번호 확인 (재인증)
        if (!user?.email) {
          throw new Error('사용자 정보를 찾을 수 없습니다')
        }

        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        })

        if (signInError) {
          throw new Error('현재 비밀번호가 올바르지 않습니다')
        }
      }

      // 비밀번호 업데이트
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        throw new Error('비밀번호 변경에 실패했습니다')
      }

      setSuccess('비밀번호가 성공적으로 변경되었습니다')

      // 폼 초기화
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')

      // 성공 메시지를 3초 후 자동으로 제거
      setTimeout(() => {
        setSuccess('')
      }, 3000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex h-screen bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="max-w-4xl mx-auto p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">설정</h1>
            <p className="mt-2 text-sm text-gray-600">
              계정 설정 및 비밀번호를 관리하세요
            </p>
          </div>

          {/* 사용자 정보 섹션 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light">
                <span className="material-symbols-outlined text-primary">account_circle</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">사용자 정보</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="material-symbols-outlined text-gray-400 text-base">person</span>
                <span className="text-gray-600 font-medium">이름:</span>
                <span className="text-gray-900">{userInfo?.name || '알 수 없음'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="material-symbols-outlined text-gray-400 text-base">mail</span>
                <span className="text-gray-600 font-medium">이메일:</span>
                <span className="text-gray-900">{user?.email || '알 수 없음'}</span>
              </div>
            </div>
          </div>

          {/* 비밀번호 변경 섹션 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light">
                <span className="material-symbols-outlined text-primary">lock</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">비밀번호 변경</h2>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">error</span>
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">check_circle</span>
                  <span className="text-sm">{success}</span>
                </div>
              )}

              {isPasswordRecovery && (
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">info</span>
                  <span className="text-sm">비밀번호 재설정 모드입니다. 현재 비밀번호 없이 새 비밀번호를 설정할 수 있습니다.</span>
                </div>
              )}

              {!isPasswordRecovery && (
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    현재 비밀번호
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-sm">key</span>
                    </div>
                    <input
                      id="currentPassword"
                      type="password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={loading || !!success}
                      className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="현재 비밀번호를 입력하세요"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  새 비밀번호
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="material-symbols-outlined text-gray-400 text-sm">lock_reset</span>
                  </div>
                  <input
                    id="newPassword"
                    type="password"
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading || !!success}
                    className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                    placeholder="새 비밀번호를 입력하세요"
                  />
                </div>
                <PasswordStrengthIndicator password={newPassword} />
                <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">info</span>
                  <span>최소 6자 이상, 대소문자와 숫자, 특수문자를 포함하면 더 안전합니다</span>
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  새 비밀번호 확인
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="material-symbols-outlined text-gray-400 text-sm">check_circle</span>
                  </div>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading || !!success}
                    className="form-input block w-full pl-10 px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
                    placeholder="새 비밀번호를 다시 입력하세요"
                  />
                </div>
                {newPassword && confirmPassword && (
                  <div className="mt-2">
                    {newPassword === confirmPassword ? (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">check_circle</span>
                        <span>비밀번호가 일치합니다</span>
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">cancel</span>
                        <span>비밀번호가 일치하지 않습니다</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={loading || !!success}
                  className="flex items-center gap-2 px-6 py-2.5 border border-transparent rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                      <span>변경 중...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-base">save</span>
                      <span>변경</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  )
}
